import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetchJson } from "../apiClient";

const withCursor = (path, cursor) =>
  `${path}${path.includes("?") ? "&" : "?"}cursor=${encodeURIComponent(cursor)}`;

const errorMessage = (e) => (e && e.message) || String(e);

/** Cursor-paged list ("load more").
 *
 * `path` is the endpoint of the first page; `null`/`undefined` means idle (no
 * request at all). The API answers a page as `{items: [...], next_cursor:
 * string|null}`. `loadMore()` appends the next page, `reload()` replaces the
 * list with the first page again.
 *
 * A generation counter drops responses that belong to a superseded path or a
 * superseded reload, so a late answer of the previous path can never land in
 * the current list. Errors keep whatever is already loaded and are reported
 * through `error`.
 */
export default function usePagedList(path) {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [error, setError] = useState("");
  const generation = useRef(0);

  const fetchPage = useCallback(
    async (cursor, append) => {
      if (!path) return;
      const gen = generation.current;
      setLoading(true);
      try {
        const data = await apiFetchJson(cursor ? withCursor(path, cursor) : path);
        if (gen !== generation.current) return;
        setItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
        setNextCursor(data.next_cursor || null);
        setError("");
      } catch (e) {
        if (gen === generation.current) setError(errorMessage(e));
      } finally {
        if (gen === generation.current) setLoading(false);
      }
    },
    [path],
  );

  const reload = useCallback(() => {
    generation.current += 1;
    return fetchPage(null, false);
  }, [fetchPage]);

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setError("");
    if (!path) {
      setLoading(false);
      return undefined;
    }
    fetchPage(null, false);
    return () => {
      generation.current += 1;
    };
  }, [path, fetchPage]);

  const loadMore = useCallback(
    () => (nextCursor ? fetchPage(nextCursor, true) : undefined),
    [nextCursor, fetchPage],
  );

  return { items, loading, error, hasMore: nextCursor !== null, loadMore, reload };
}
